import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSupabase } from "@/lib/supabase-lazy";
import { useAuth } from "@/hooks/use-auth";

export type AccessLevel = "none" | "guest" | "trial" | "paid" | "expired";

export type Entitlement = {
  trial_active: boolean;
  trial_expires_at: string | null;
  paid_active: boolean;
  plan_id: string | null;
  subscription_expires_at: string | null;
  simulation_access: boolean;
  /** Persistent simulation credits; unaffected by subscription expiry. */
  simulation_credits: number;
  access_level: AccessLevel;
};

export const SIGNED_OUT_ENTITLEMENT: Entitlement = {
  trial_active: false,
  trial_expires_at: null,
  paid_active: false,
  plan_id: null,
  subscription_expires_at: null,
  simulation_access: false,
  simulation_credits: 0,
  access_level: "none",
};

type EntitlementState = {
  entitlement: Entitlement;
  loading: boolean;
  error: string | null;
  /** Timestamp the current access level expires at (trial or subscription), if any. */
  expiresAt: string | null;
  refresh: () => void;
};

const EntitlementContext = createContext<EntitlementState | null>(null);

function normalize(raw: unknown): Entitlement {
  const value = (raw ?? {}) as Partial<Entitlement>;
  return {
    // The 24-hour trial is disabled: trial facts are always inert.
    trial_active: false,
    trial_expires_at: null,
    paid_active: value.paid_active === true,
    plan_id: value.plan_id ?? null,
    subscription_expires_at: value.subscription_expires_at ?? null,
    simulation_access: value.simulation_access === true,
    simulation_credits: Math.max(0, Number(value.simulation_credits ?? 0)),
    access_level:
      value.access_level === "guest" || value.access_level === "none"
        ? value.access_level
        : value.paid_active === true
          ? "paid"
          : "expired",
  };
}


/** The single source of truth: one RPC round-trip per fetch. */
async function fetchEntitlement(): Promise<Entitlement> {
  const supabase = await getSupabase();
  // No trial setup call: the 24-hour trial is permanently disabled and every
  // non-paying account simply uses the free tier.
  const { data, error } = await supabase.rpc("get_user_entitlement");
  if (error) throw new Error(error.message);
  return normalize(data);
}

function activeExpiry(e: Entitlement): string | null {
  if (e.paid_active) return e.subscription_expires_at;
  return null;
}


export function EntitlementProvider({ children }: { children: ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;

  const query = useQuery({
    queryKey: ["entitlement", userId],
    queryFn: () => fetchEntitlement(),
    enabled: Boolean(userId),
    // Fetched once per page load / auth change / window focus — never polled.
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    refetchInterval: false,
    retry: 1,
  });

  const entitlement = userId ? (query.data ?? SIGNED_OUT_ENTITLEMENT) : SIGNED_OUT_ENTITLEMENT;
  const expiresAt = activeExpiry(entitlement);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["entitlement"] });
  }, [queryClient]);

  // Flip state exactly when the trial/subscription ends, without polling.
  useEffect(() => {
    if (!expiresAt) return;
    const remaining = new Date(expiresAt).getTime() - Date.now();
    if (!Number.isFinite(remaining)) return;
    const delay = Math.max(0, remaining) + 1_000;
    if (delay > 2_147_483_000) return;
    const timer = window.setTimeout(refresh, delay);
    return () => window.clearTimeout(timer);
  }, [expiresAt, refresh]);

  const value = useMemo<EntitlementState>(
    () => ({
      entitlement,
      loading: authLoading || (Boolean(userId) && query.isLoading),
      error: query.error instanceof Error ? query.error.message : null,
      expiresAt,
      refresh,
    }),
    [entitlement, authLoading, userId, query.isLoading, query.error, expiresAt, refresh],
  );

  return <EntitlementContext.Provider value={value}>{children}</EntitlementContext.Provider>;
}

export function useEntitlement(): EntitlementState {
  const ctx = useContext(EntitlementContext);
  if (!ctx) throw new Error("useEntitlement must be used inside EntitlementProvider");
  return ctx;
}

/**
 * Local ticking countdown against a fixed authoritative timestamp.
 * No database calls while it ticks.
 */
export function useCountdown(expiresAt: string | null): {
  msRemaining: number;
  expired: boolean;
  label: string;
} {
  const target = expiresAt ? new Date(expiresAt).getTime() : null;
  const compute = useCallback(
    () => (target === null || !Number.isFinite(target) ? 0 : Math.max(0, target - Date.now())),
    [target],
  );
  const [msRemaining, setMsRemaining] = useState(compute);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    setMsRemaining(compute());
    if (target === null) return;
    const id = window.setInterval(() => setMsRemaining(compute()), 1_000);
    frame.current = id;
    return () => window.clearInterval(id);
  }, [compute, target]);

  const label = useMemo(() => {
    if (target === null) return "—";
    const total = Math.floor(msRemaining / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [msRemaining, target]);

  return { msRemaining, expired: target !== null && msRemaining <= 0, label };
}